import React, { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Upload, CheckCircle2, AlertTriangle, Copy, RotateCcw, Lock, FileText, Sparkles, Link2, UserPlus, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { API } from "@/lib/api";

export default function Imports() {
  const { can } = useAuth();
  const [mode, setMode] = useState("policy");
  if (!can.write) return (
    <div className="p-6 animate-fade-in"><PageHeader title="Import Center" />
      <div className="bg-white border border-border rounded-sm p-10 text-center text-mutedsea" data-testid="imports-readonly-denied"><Lock className="h-8 w-8 mx-auto mb-2 text-sky" />Read-only access — importing requires write permission.</div>
    </div>
  );
  return (
    <div className="p-6 animate-fade-in">
      <PageHeader title="Import Center" subtitle="Import structured client/policy data or ingest policy documents with review, matching and reconciliation." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <button data-testid="import-mode-policy" onClick={() => setMode("policy")}
          className={`text-left p-4 rounded-sm border-2 transition-all ${mode === "policy" ? "border-atlantic bg-atlantic/5" : "border-border bg-white hover:border-atlantic/40"}`}>
          <div className="flex items-center gap-2 font-serif text-lg text-navy"><FileText className="h-5 w-5 text-atlantic" /> Policy Document</div>
          <p className="text-sm text-mutedsea mt-1">PDF · Word · scanned document — AI extracts the policy, you review, then commit.</p>
        </button>
        <button data-testid="import-mode-structured" onClick={() => setMode("structured")}
          className={`text-left p-4 rounded-sm border-2 transition-all ${mode === "structured" ? "border-atlantic bg-atlantic/5" : "border-border bg-white hover:border-atlantic/40"}`}>
          <div className="flex items-center gap-2 font-serif text-lg text-navy"><Upload className="h-5 w-5 text-atlantic" /> Structured Data</div>
          <p className="text-sm text-mutedsea mt-1">CSV · XLSX bulk migration with mapping, validation, duplicate detection & reconciliation.</p>
        </button>
      </div>
      {mode === "policy" ? <PolicyDocImport /> : <StructuredImport />}
    </div>
  );
}

function StructuredImport() {
  const { can } = useAuth();
  const [step, setStep] = useState(1);
  const [parsed, setParsed] = useState(null);
  const [importType, setImportType] = useState("parties");
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadBatches = async () => { try { const { data } = await api.get("/imports/batches"); setBatches(data.items); } catch {} };
  useEffect(() => { loadBatches(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/imports/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data);
      const tplFields = data.templates[importType] || [];
      const auto = {};
      tplFields.forEach((f) => { const m = data.columns.find((c) => c.toLowerCase().replace(/[^a-z]/g, "") === f.toLowerCase().replace(/[^a-z]/g, "")); if (m) auto[f] = m; });
      setMapping(auto);
      setStep(2);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not parse file."); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/imports/commit", { import_type: importType, mapping, rows: parsed.rows });
      setResult(data); setStep(3); loadBatches();
      toast.success(`Import complete: ${data.success_count} imported, ${data.exception_count} exceptions, ${data.duplicate_count} duplicates.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Import failed."); }
    finally { setBusy(false); }
  };

  const rollback = async (b) => {
    if (!window.confirm(`Roll back import ${b.batch_ref}? This removes ${b.success_count} records.`)) return;
    try { await api.post(`/imports/${b.id}/rollback`); toast.success("Rolled back."); loadBatches(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const reset = () => { setStep(1); setParsed(null); setMapping({}); setResult(null); };
  const tplFields = parsed?.templates?.[importType] || [];

  if (!can.write) return (
    <div className="p-6 animate-fade-in">
      <PageHeader title="Import Center" />
      <div className="bg-white border border-border rounded-sm p-10 text-center" data-testid="imports-access-denied">
        <Lock className="h-8 w-8 mx-auto text-mutedsea mb-2" />
        <p className="text-slate font-medium">Write access required</p>
        <p className="text-sm text-mutedsea mt-1">You do not have permission to import records.</p>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 text-sm text-mutedsea"><Upload className="h-4 w-4" /> Structured Data Import — CSV / XLSX</div>

      <div className="bg-white border border-border rounded-sm p-5 mb-6">
        <div className="flex gap-6 mb-4 text-sm">
          {[[1, "Upload"], [2, "Map & Validate"], [3, "Reconciliation"]].map(([n, l]) => (
            <div key={n} className={`flex items-center gap-2 ${step >= n ? "text-navy font-medium" : "text-mutedsea"}`}>
              <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs ${step >= n ? "bg-navy text-white" : "bg-ice text-mutedsea"}`}>{n}</span>{l}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <label className="text-sm text-slate">Import type:</label>
              <Select value={importType} onValueChange={setImportType}>
                <SelectTrigger className="w-56 bg-white" data-testid="import-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="parties">Clients (Parties)</SelectItem><SelectItem value="policies">Policies</SelectItem></SelectContent>
              </Select>
            </div>
            <label className="block border-2 border-dashed border-sky rounded-sm p-10 text-center cursor-pointer hover:bg-ice nav-link" data-testid="import-dropzone">
              <Upload className="h-8 w-8 mx-auto text-atlantic mb-2" />
              <span className="text-sm text-slate">{busy ? "Parsing…" : "Click to choose a CSV or XLSX file"}</span>
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="import-file" disabled={!can.write} />
            </label>
          </div>
        )}

        {step === 2 && parsed && (
          <div>
            <p className="text-sm text-mutedsea mb-3">{parsed.row_count} rows detected. Map internal fields to your columns.</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {tplFields.map((f) => (
                <div key={f} className="flex items-center gap-2">
                  <label className="text-sm text-slate w-40 truncate">{f}</label>
                  <Select value={mapping[f] || "__none"} onValueChange={(v) => setMapping((m) => ({ ...m, [f]: v === "__none" ? "" : v }))}>
                    <SelectTrigger className="flex-1 bg-white h-9 text-sm" data-testid={`map-${f}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-60"><SelectItem value="__none">— skip —</SelectItem>{parsed.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={reset}>Cancel</Button>
              <Button onClick={commit} disabled={busy} data-testid="import-commit" className="bg-navy hover:bg-atlantic text-white">{busy ? "Importing…" : "Validate & Import"}</Button>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <Stat icon={CheckCircle2} tone="text-seagreen" n={result.success_count} label="Imported" />
              <Stat icon={Copy} tone="text-seaamber" n={result.duplicate_count} label="Duplicates (not imported)" />
              <Stat icon={AlertTriangle} tone="text-seared" n={result.exception_count} label="Exceptions" />
            </div>
            {(result.duplicates?.length > 0 || result.exceptions?.length > 0) && (
              <div className="border border-border rounded-sm max-h-60 overflow-y-auto end-scroll mb-4">
                {result.exceptions.map((x, i) => <div key={`e${i}`} className="px-3 py-1.5 text-sm border-b border-border text-seared">Row {x.row}: {x.reason}</div>)}
                {result.duplicates.map((x, i) => <div key={`d${i}`} className="px-3 py-1.5 text-sm border-b border-border text-seaamber">Row {x.row}: {x.reason} (existing {x.existing_id?.slice(0, 8)})</div>)}
              </div>
            )}
            <Button onClick={reset} className="bg-navy hover:bg-atlantic text-white" data-testid="import-again">Import Another File</Button>
          </div>
        )}
      </div>

      <h2 className="font-serif text-2xl text-navy mb-3">Import History</h2>
      <div className="bg-white border border-border rounded-sm overflow-x-auto end-scroll">
        <table className="w-full text-sm">
          <thead><tr className="bg-cloud border-b border-border">{["Batch", "Type", "Rows", "Imported", "Dupes", "Exceptions", "Reviewer", ""].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold">{h}</th>)}</tr></thead>
          <tbody>
            {batches.length === 0 ? <tr><td colSpan={8} className="px-3 py-6 text-center text-mutedsea">No imports yet.</td></tr> :
              batches.map((b) => (
                <tr key={b.id} className="border-b border-border last:border-0" data-testid={`batch-${b.id}`}>
                  <td className="px-3 py-2">{b.batch_ref}</td><td className="px-3 py-2">{b.import_type}</td>
                  <td className="px-3 py-2">{b.total_rows}</td><td className="px-3 py-2 text-seagreen">{b.success_count}</td>
                  <td className="px-3 py-2 text-seaamber">{b.duplicate_count}</td>
                  <td className="px-3 py-2">{b.exception_count > 0 ? <Badge variant="outline" className="bg-seared/10 text-seared border-seared/30">{b.exception_count}</Badge> : 0}</td>
                  <td className="px-3 py-2 text-xs text-mutedsea">{b.reviewer}</td>
                  <td className="px-3 py-2 text-right">{can.admin && !b.rolled_back && <Button size="sm" variant="ghost" onClick={() => rollback(b)} data-testid={`rollback-${b.id}`} className="h-7 text-xs text-seared"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Rollback</Button>}{b.rolled_back && <span className="text-xs text-mutedsea">Rolled back</span>}</td>
                </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, tone, n, label }) => (
  <div className="border border-border rounded-sm p-4 text-center">
    <Icon className={`h-6 w-6 mx-auto ${tone}`} />
    <div className="font-serif text-3xl text-navy mt-1">{n}</div>
    <div className="text-xs text-mutedsea">{label}</div>
  </div>
);

const FIELD_GROUPS = [
  ["Client / Named Insured", [["named_insured", "Named Insured"], ["additional_named_insured", "Additional Named Insured"], ["dba", "DBA"], ["entity_type", "Entity Type"], ["mailing_address", "Mailing Address"], ["risk_address", "Risk Address"], ["email", "Email"], ["phone", "Phone"]]],
  ["Policy", [["carrier_name", "Carrier"], ["naic", "NAIC"], ["policy_number", "Policy Number"], ["line_of_business", "Line of Business"], ["policy_type", "Policy Type"], ["effective_date", "Effective Date"], ["expiration_date", "Expiration Date"], ["policy_status", "Policy Status"], ["transaction_type", "New / Renewal / Rewrite"]]],
  ["Premium", [["annual_premium", "Annual Premium"], ["taxes", "Taxes"], ["fees", "Fees"], ["total_premium", "Total Premium"], ["payment_info", "Payment Info"]]],
  ["Producer / Agency", [["producer", "Producer"], ["agency", "Agency"]]],
];

function PolicyDocImport() {
  const [staging, setStaging] = useState(null);
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [clientAction, setClientAction] = useState("create");
  const [partyId, setPartyId] = useState("");
  const [committed, setCommitted] = useState(null);
  // Phase 2 — renewal / endorsement
  const [kind, setKind] = useState("new");
  const [targetId, setTargetId] = useState("");
  const [override, setOverride] = useState(false);
  const [delta, setDelta] = useState("");
  const [covRows, setCovRows] = useState([]);   // endorsement/renewal coverage changes w/ accept flag
  const [fieldRows, setFieldRows] = useState([]);

  const target = (staging?.lineage || []).find((l) => l.policy.id === targetId) || (staging?.lineage || [])[0];
  const currentTerm = target?.current_term || {};

  useEffect(() => {
    if (kind === "new" || !target?.diff) return;
    setDelta(target.diff.premium_delta ?? "");
    setCovRows((target.diff.coverages || []).map((c) => ({ ...c, accept: true })));
    setFieldRows((target.diff.fields || []).filter((f) => f.changed).map((f) => ({ ...f, accept: true })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, kind]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setCommitted(null); setOverride(false);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/policy-import/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setStaging(data); setFields({ ...data.extracted });
      const k = data.import_kind || "new";
      setKind(k);
      if (data.matches?.length) { setClientAction("link"); setPartyId(data.matches[0].id); } else setClientAction("create");
      if (k !== "new" && data.lineage?.length) {
        setTargetId(data.lineage[0].policy.id);
        setDelta(data.diff?.premium_delta ?? "");
        setCovRows((data.diff?.coverages || []).map((c) => ({ ...c, accept: true })));
        setFieldRows((data.diff?.fields || []).filter((f) => f.changed).map((f) => ({ ...f, accept: true })));
      } else if (k !== "new") {
        setTargetId("");
      }
      toast.success(`Extracted — ${k === "new" ? "new policy" : k} (${data.extracted.confidence || "REVIEW"}). Review before committing.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload/extract failed."); }
    finally { setBusy(false); }
  };
  const setF = (k, v) => setFields((p) => ({ ...p, [k]: v }));
  const discard = async () => {
    const sid = staging?.id;
    setStaging(null);
    if (sid) {
      try { await axios.post(`${API}/policy-import/discard`, { staging_id: sid }, { withCredentials: true }); }
      catch (e) { /* staging already gone or committed — safe to ignore */ }
    }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/policy-import/commit`, {
        staging_id: staging.id, client_action: clientAction, party_id: partyId,
        category: "Policies", fields,
      }, { withCredentials: true });
      setCommitted(data); setStaging(null);
      toast.success("Policy imported — client, policy, chronology and filing cabinet updated.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Commit failed."); }
    finally { setBusy(false); }
  };
  const commitRenewal = async () => {
    if (!targetId) { toast.error("Select the policy this renewal belongs to."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/policy-import/commit`, {
        staging_id: staging.id, import_mode: "renewal", policy_id: targetId, override,
        fields: { ...fields },
        coverage_changes: covRows.filter((c) => c.accept),
      }, { withCredentials: true });
      setCommitted(data); setStaging(null);
      toast.success("Renewal imported — prior term preserved, new term in force.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Renewal import failed."); }
    finally { setBusy(false); }
  };
  const commitEndorsement = async () => {
    if (!targetId) { toast.error("Select the policy this endorsement applies to."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/policy-import/commit`, {
        staging_id: staging.id, import_mode: "endorsement", policy_id: targetId,
        policy_term_id: currentTerm.id || "", override,
        endorsement: {
          number: fields.endorsement_number || "", effective_date: fields.endorsement_effective_date || fields.effective_date || "",
          premium_delta: delta === "" ? "" : delta,
          coverage_changes: covRows.filter((c) => c.accept),
          field_changes: fieldRows.filter((f) => f.accept),
        },
      }, { withCredentials: true });
      setCommitted(data); setStaging(null);
      toast.success("Endorsement applied — event recorded and current policy updated.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Endorsement import failed."); }
    finally { setBusy(false); }
  };

  if (committed) return (
    <div className="bg-white border border-seagreen/40 rounded-sm p-8 text-center" data-testid="policy-import-done">
      <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-seagreen" />
      <p className="text-slate font-medium mb-1">Policy imported successfully.</p>
      <p className="text-sm text-mutedsea mb-4">Client record, policy, chronology entry and filing-cabinet document were created/updated.</p>
      <div className="flex gap-2 justify-center">
        <Button onClick={() => window.location.assign(`/clients/${committed.party_id}`)} className="bg-navy text-white" data-testid="open-client">Open Client</Button>
        <Button variant="outline" onClick={() => setCommitted(null)}>Import Another</Button>
      </div>
    </div>
  );

  if (!staging) return (
    <div data-testid="policy-import-upload"
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
      className={`bg-white border-2 border-dashed rounded-sm p-12 text-center ${drag ? "border-atlantic bg-atlantic/5" : "border-border"}`}>
      <FileText className="h-10 w-10 mx-auto mb-3 text-atlantic" />
      <p className="text-slate font-medium mb-1">Import a policy document</p>
      <p className="text-sm text-mutedsea mb-4">Drag & drop or select a PDF, Word, scanned PDF or image. The original is preserved in the client's filing cabinet.</p>
      <label className="inline-flex items-center gap-2 px-4 py-2 bg-navy hover:bg-atlantic text-white rounded-sm cursor-pointer text-sm">
        <Sparkles className="h-4 w-4" /> {busy ? "Extracting…" : "Select policy document"}
        <input type="file" data-testid="policy-file-input" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.tif,.tiff" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
      </label>
    </div>
  );

  const conf = fields.confidence || staging.extracted?.confidence;
  return (
    <div className="space-y-4" data-testid="policy-import-review">
      <div className="flex items-center justify-between bg-white border border-border rounded-sm p-3">
        <div className="text-sm text-slate flex items-center gap-2"><FileText className="h-4 w-4 text-atlantic" /> {staging.filename}
          <Badge variant="outline" className={conf === "HIGH" ? "bg-seagreen/10 text-seagreen border-seagreen/30" : "bg-seaamber/10 text-seaamber border-seaamber/40"}>{conf || "REVIEW REQUIRED"}</Badge>
          {staging.used_vision && <Badge variant="outline" className="bg-cloud text-mutedsea border-border">scanned · vision OCR</Badge>}
        </div>
        <Button variant="outline" size="sm" data-testid="policy-discard-top" onClick={discard}>Discard</Button>
      </div>

      {/* Phase 2 — document kind (renewal / endorsement / new) */}
      <div className="bg-white border border-border rounded-sm p-4" data-testid="import-kind">
        <div className="font-serif text-base text-navy mb-2">This document is a…</div>
        <div className="flex flex-wrap gap-2">
          {[["new", "New policy"], ["renewal", "Renewal"], ["endorsement", "Endorsement"]].map(([k, label]) => (
            <button key={k} data-testid={`kind-${k}`} onClick={() => setKind(k)}
              className={`px-3 py-1.5 rounded-sm border text-sm ${kind === k ? "border-atlantic bg-atlantic/10 text-navy" : "border-border bg-white text-mutedsea hover:border-atlantic/40"}`}>{label}</button>
          ))}
          <Badge variant="outline" className="bg-cloud text-mutedsea border-border ml-1" data-testid="import-kind-detected">AI detected: {staging.import_kind || "new"}</Badge>
        </div>
      </div>

      {kind !== "new" && (
        <div className="bg-white border border-border rounded-sm p-4" data-testid="lineage-target">
          <div className="font-serif text-lg text-navy mb-2 flex items-center gap-2"><Link2 className="h-4 w-4 text-atlantic" /> {kind === "renewal" ? "Renewing policy" : "Endorsing policy"}</div>
          {staging.lineage?.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-mutedsea">Select the existing policy lineage this {kind} belongs to — a new duplicate policy will not be created.</p>
              {staging.lineage.map((l) => (
                <label key={l.policy.id} data-testid={`lineage-${l.policy.id}`} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="lin" checked={targetId === l.policy.id} onChange={() => setTargetId(l.policy.id)} />
                  <span className="text-slate">{l.policy.policy_number || "(no #)"} · {l.policy.carrier_name}</span>
                  <Badge variant="outline" className="bg-cloud text-mutedsea border-border">{l.party?.display_name || "—"}</Badge>
                  <span className="text-xs text-mutedsea">{l.policy.line || ""}{l.current_term?.effective_date ? ` · term ${l.current_term.effective_date}` : ""}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-sm text-seaamber flex items-center gap-2" data-testid="lineage-none"><AlertTriangle className="h-4 w-4" /> No matching existing policy found. Review the details, or switch to <b>New policy</b> above to create one.</div>
          )}
        </div>
      )}

      {/* Renewal comparison: prior term vs proposed renewal */}
      {kind === "renewal" && target && (
        <div className="bg-white border border-border rounded-sm p-4" data-testid="renewal-review">
          <div className="font-serif text-base text-navy mb-3">Prior term → Proposed renewal</div>
          <div className="grid grid-cols-3 gap-2 text-sm items-center">
            <div className="text-xs text-mutedsea uppercase">Field</div>
            <div className="text-xs text-mutedsea uppercase">Prior term (preserved)</div>
            <div className="text-xs text-mutedsea uppercase">Proposed renewal</div>
            {[["carrier_name", "Carrier", target.policy.carrier_name], ["effective_date", "Effective", currentTerm.effective_date], ["expiration_date", "Expiration", currentTerm.expiration_date]].map(([k, label, prior]) => (
              <React.Fragment key={k}>
                <div className="text-slate">{label}</div>
                <div className="text-mutedsea line-through decoration-mutedsea/40">{prior || "—"}</div>
                <Input data-testid={`ren-${k}`} className="bg-white text-sm h-8" value={fields[k] || ""} onChange={(e) => setF(k, e.target.value)} />
              </React.Fragment>
            ))}
            <div className="text-slate">Premium</div>
            <div className="text-mutedsea line-through decoration-mutedsea/40">{currentTerm.premium != null ? `$${currentTerm.premium}` : "—"}</div>
            <Input data-testid="ren-premium" className="bg-white text-sm h-8" value={fields.total_premium || fields.annual_premium || ""} onChange={(e) => setF("total_premium", e.target.value)} />
          </div>
          {covRows.length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-mutedsea uppercase mb-1">Coverage changes</div>
              {covRows.map((c, i) => (
                <label key={i} data-testid={`ren-cov-${i}`} className="flex items-center gap-2 text-sm mb-1">
                  <input type="checkbox" checked={c.accept} onChange={(e) => setCovRows((r) => r.map((x, j) => j === i ? { ...x, accept: e.target.checked } : x))} />
                  <Badge variant="outline" className={c.action === "add" ? "bg-seagreen/10 text-seagreen border-seagreen/30" : c.action === "remove" ? "bg-red-50 text-red-600 border-red-200" : "bg-seaamber/10 text-seaamber border-seaamber/40"}>{c.action}</Badge>
                  <span className="text-slate">{c.coverage_type}</span>
                  <span className="text-xs text-mutedsea">{c.current ? `was ${c.current.limit || "—"}/${c.current.deductible || "—"}` : ""} {c.proposed ? `→ ${c.proposed.limit || "—"}/${c.proposed.deductible || "—"}` : ""}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Endorsement review: current vs proposed, accept/reject each change */}
      {kind === "endorsement" && target && (
        <div className="bg-white border border-border rounded-sm p-4" data-testid="endorsement-review">
          <div className="font-serif text-base text-navy mb-3">Mid-term endorsement — current → proposed</div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <Label className="text-xs mb-1">Endorsement #</Label>
              <Input data-testid="end-number" className="bg-white text-sm h-8" value={fields.endorsement_number || ""} onChange={(e) => setF("endorsement_number", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs mb-1">Effective date</Label>
              <Input data-testid="end-eff" className="bg-white text-sm h-8" value={fields.endorsement_effective_date || fields.effective_date || ""} onChange={(e) => setF("endorsement_effective_date", e.target.value)} />
            </div>
          </div>
          <div className="border border-border rounded-sm p-3 mb-3 bg-cloud/40">
            <div className="text-xs text-mutedsea uppercase mb-1">Premium change</div>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-slate">Current <b>${currentTerm.premium ?? 0}</b></span>
              <span className="text-mutedsea">delta</span>
              <Input data-testid="end-delta" className="bg-white text-sm h-8 w-28" value={delta} placeholder="+/- amount" onChange={(e) => setDelta(e.target.value)} />
              <span className="text-navy font-medium" data-testid="end-premium-after">→ ${(Number(currentTerm.premium || 0) + Number(delta || 0)).toFixed(2)}</span>
            </div>
          </div>
          {covRows.length > 0 && (
            <div className="mb-3">
              <div className="text-xs text-mutedsea uppercase mb-1">Coverage changes</div>
              {covRows.map((c, i) => (
                <label key={i} data-testid={`end-cov-${i}`} className="flex items-center gap-2 text-sm mb-1">
                  <input type="checkbox" checked={c.accept} onChange={(e) => setCovRows((r) => r.map((x, j) => j === i ? { ...x, accept: e.target.checked } : x))} />
                  <Badge variant="outline" className={c.action === "add" ? "bg-seagreen/10 text-seagreen border-seagreen/30" : c.action === "remove" ? "bg-red-50 text-red-600 border-red-200" : "bg-seaamber/10 text-seaamber border-seaamber/40"}>{c.action}</Badge>
                  <span className="text-slate">{c.coverage_type}</span>
                  <span className="text-xs text-mutedsea">{c.current ? `was ${c.current.limit || "—"}/${c.current.deductible || "—"}` : ""} {c.proposed ? `→ ${c.proposed.limit || "—"}/${c.proposed.deductible || "—"}` : ""}</span>
                </label>
              ))}
            </div>
          )}
          {fieldRows.length > 0 && (
            <div>
              <div className="text-xs text-mutedsea uppercase mb-1">Other changes</div>
              {fieldRows.map((f, i) => (
                <label key={i} data-testid={`end-field-${i}`} className="flex items-center gap-2 text-sm mb-1">
                  <input type="checkbox" checked={f.accept} onChange={(e) => setFieldRows((r) => r.map((x, j) => j === i ? { ...x, accept: e.target.checked } : x))} />
                  <span className="text-slate">{f.label}</span>
                  <span className="text-xs text-mutedsea line-through">{f.current || "—"}</span>
                  <span className="text-xs text-navy">→ {f.after || f.proposed || "—"}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {kind !== "new" && (
        <label className="flex items-center gap-2 text-sm text-slate" data-testid="override-check">
          <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
          Override duplicate protection (authorized users) — apply even if this {kind} appears already imported
        </label>
      )}


      {kind === "new" && (
      <div className="bg-white border border-border rounded-sm p-4">
        <div className="font-serif text-lg text-navy mb-2 flex items-center gap-2"><UserPlus className="h-4 w-4 text-atlantic" /> Client match</div>
        {staging.matches?.length > 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-seaamber">Possible existing {staging.matches.length === 1 ? "client" : "clients"} found — link instead of creating a duplicate.</p>
            {staging.matches.map((m) => (
              <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="cm" checked={clientAction !== "create" && partyId === m.id} onChange={() => { setPartyId(m.id); setClientAction(m.relationship === "Prospect" ? "convert" : "link"); }} />
                <span className="text-slate">{m.display_name}</span>
                <Badge variant="outline" className="bg-cloud text-mutedsea border-border">{m.relationship || "Client"}{m.relationship === "Prospect" ? " → convert on import" : ""}</Badge>
                <span className="text-xs text-mutedsea">{m.email || m.phone || ""}</span>
              </label>
            ))}
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="client-create-new">
              <input type="radio" name="cm" checked={clientAction === "create"} onChange={() => setClientAction("create")} />
              <span className="text-slate">Create new client</span> <Plus className="h-3.5 w-3.5 text-atlantic" />
            </label>
          </div>
        ) : (
          <p className="text-sm text-mutedsea">No existing match — a new client will be created from the reviewed details below.</p>
        )}
      </div>
      )}

      {staging.duplicate_policy && kind === "new" && (
        <div className="bg-seaamber/10 border border-seaamber/40 rounded-sm p-3 text-sm text-slate flex items-center gap-2" data-testid="dup-policy-warn">
          <AlertTriangle className="h-4 w-4 text-seaamber" /> A policy <b>{staging.duplicate_policy.policy_number}</b> ({staging.duplicate_policy.carrier_name}) already exists — this looks like a renewal/endorsement. Switch the type above, or confirm this is genuinely a new policy.
        </div>
      )}

      {kind !== "endorsement" && (<>
      {FIELD_GROUPS.map(([group, flds]) => (
        <div key={group} className="bg-white border border-border rounded-sm p-4">
          <div className="font-serif text-base text-navy mb-2">{group}</div>
          <div className="grid grid-cols-2 gap-3">
            {flds.map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs mb-1 flex items-center gap-1">{label}{!fields[k] && <span className="text-seaamber text-[10px]">· blank</span>}</Label>
                <Input data-testid={`fld-${k}`} value={fields[k] || ""} onChange={(e) => setF(k, e.target.value)} className="bg-white text-sm h-8" />
              </div>
            ))}
          </div>
        </div>
      ))}

      {fields.coverages?.length > 0 && (
        <div className="bg-white border border-border rounded-sm p-4">
          <div className="font-serif text-base text-navy mb-2">Coverages ({fields.coverages.length})</div>
          {fields.coverages.map((c, i) => (
            <div key={i} className="grid grid-cols-4 gap-2 mb-1">
              <Input className="bg-white text-xs h-8" value={c.name || ""} placeholder="Coverage" onChange={(e) => { const v = [...fields.coverages]; v[i] = { ...v[i], name: e.target.value }; setF("coverages", v); }} />
              <Input className="bg-white text-xs h-8" value={c.limit || ""} placeholder="Limit" onChange={(e) => { const v = [...fields.coverages]; v[i] = { ...v[i], limit: e.target.value }; setF("coverages", v); }} />
              <Input className="bg-white text-xs h-8" value={c.deductible || ""} placeholder="Deductible" onChange={(e) => { const v = [...fields.coverages]; v[i] = { ...v[i], deductible: e.target.value }; setF("coverages", v); }} />
              <Input className="bg-white text-xs h-8" value={c.notes || ""} placeholder="Notes" onChange={(e) => { const v = [...fields.coverages]; v[i] = { ...v[i], notes: e.target.value }; setF("coverages", v); }} />
            </div>
          ))}
        </div>
      )}
      </>)}

      <div className="flex justify-end gap-2">
        <Button variant="outline" data-testid="policy-cancel" onClick={discard}>Cancel</Button>
        {kind === "renewal" ? (
          <Button data-testid="commit-renewal" onClick={commitRenewal} disabled={busy || !targetId} className="bg-navy hover:bg-atlantic text-white"><RotateCcw className="h-4 w-4 mr-1.5" /> {busy ? "Importing…" : "Commit Renewal"}</Button>
        ) : kind === "endorsement" ? (
          <Button data-testid="commit-endorsement" onClick={commitEndorsement} disabled={busy || !targetId} className="bg-navy hover:bg-atlantic text-white"><Link2 className="h-4 w-4 mr-1.5" /> {busy ? "Applying…" : "Apply Endorsement"}</Button>
        ) : (
          <Button data-testid="policy-commit" onClick={commit} disabled={busy} className="bg-navy hover:bg-atlantic text-white"><Link2 className="h-4 w-4 mr-1.5" /> {busy ? "Committing…" : "Commit to CRM"}</Button>
        )}
      </div>
    </div>
  );
}
